import * as React from 'react';
import { createPortal } from 'react-dom';
import { loadModules, setDefaultOptions } from 'esri-loader';
import DefaultContactCategoryMapColors, {
    CONTACT_CATEGORY_WHITELIST,
    CONTACT_CATEGORY_LEGEND_ORDER,
    CONTACT_CATEGORY_OTHER_LABEL,
    CONTACT_CATEGORY_OTHER_COLOR,
    contactCategoryKey,
    contactCategoryValueExpression,
} from './DefaultContactCategoryMapColors';
import {
    canonicalTitleStatusValue,
    titleStatusColor,
    titleStatusValueExpression,
    titleStatusLegendRank,
    NO_TITLE_LABEL,
    NO_TITLE_COLOR,
} from './DefaultTitleStatusMapColors';
import { LeaseholdOwnerPalette, UnleasedColor, DefaultColor as LeaseholdDefaultColor, ClientOwnedColor } from './DefaultLeaseholdMapColors';
import { installArcgisCanvasReadbackFix } from './ArcgisCanvasReadback';
import { loadMapConfig, normalizeRgba, IMapConfig, ITractLayerConfig, isTractLayerEnabled, tractLayerConfigKey } from './MapConfig';
import { createAutoAssignState, resolveColor, resolvePredefined } from './ColorResolver';
import { firstPerTract } from './tractIdentity';
import { MapLoadStep } from './mapHealth';
import {
    compositionSwatches,
    contactCategorySwatches,
    HoverDescriber,
    hoverTipPosition,
    IHoverSwatch,
    peekNeedsSelectedLookup,
    peekTractId,
    swatchCss,
    titleStatusSwatches,
} from './tractHoverTip';
import {
    collectLeaseholdValues,
    contractLayerFields,
    contractLayerTitle,
    contractLayerValueExpression,
    ILeaseholdValues,
    LEASEHOLD_LAYER_TITLE,
    OIL_AND_GAS_LAYER_FIELDS,
    readContractLayerManifest,
} from './ContractLayers';

import './MapStyling.scss';

/* Linger-to-peek tooltip: how long the mouse must rest on a tract before it fades in, and how long it
 * takes to fade out once the mouse moves (.map-hover-tip.is-leaving). */
const HOVER_PEEK_DELAY_MS = 500;
const HOVER_FADE_MS = 180;

/* The legend drawer's open/close animation ($legend-anim in MapStyling.scss), with a little room. */
const LEGEND_DRAWER_MS = 240;

// defining custom interfaces to store needed data that is not included with the arcgis current version import
interface IUniqueValueInfoProperties {
    value?: string;
    symbol?: __esri.SimpleFillSymbol | __esri.PictureFillSymbol | __esri.CIMSymbol;
    label?: string;
}

interface ICIMSolidStroke {
    type: 'CIMSolidStroke';
    enable?: boolean;
    width?: number;
    color?: number[];
}

interface ICIMLineSymbol {
    type: 'CIMLineSymbol';
    symbolLayers: ICIMSolidStroke[];
}

interface ICIMHatchFill {
    type: 'CIMHatchFill';
    enable?: boolean;
    lineSymbol: ICIMLineSymbol;
    rotation?: number;
    separation?: number;
    offsetX?: number;
    offsetY?: number;
}

interface ICIMSolidFill {
    type: 'CIMSolidFill';
    enable?: boolean;
    color?: number[];
}

type CIMSymbolLayerType = ICIMHatchFill | ICIMSolidFill | ICIMSolidStroke;

/**
 * Load the basemap and every operational layer, resolving even when some of them fail.
 * ArcGIS keeps a failed layer in `map.allLayers` in a `loadStatus: 'failed'` state, where
 * it renders nothing but still produces a broken LayerList row, so failures are pulled out
 * of their parent group as well as logged. A layer 404ing upstream is a content problem in
 * the portal item, not a reason to deny the user the other 16 layers.
 */
async function loadLayersTolerantly(map: __esri.WebMap): Promise<void> {
    const loadOne = async (layer: __esri.Layer | __esri.Basemap) => {
        try {
            await layer.load();
            return null;
        } catch (err) {
            return { layer, err };
        }
    };

    const basemapFailure = await loadOne(map.basemap);
    if (basemapFailure) {
        console.log('[MapLegacy] basemap failed to load:', basemapFailure.err);
    }

    const failures = (await Promise.all(map.allLayers.toArray().map(loadOne))).filter(Boolean) as Array<{
        layer: __esri.Layer;
        err: unknown;
    }>;

    failures.forEach(({ layer, err }) => {
        const url = (layer as __esri.FeatureLayer).url;
        console.log(`[MapLegacy] dropping layer "${layer.title ?? layer.id}"${url ? ` (${url})` : ''}:`, err);
        try {
            const parent = (layer as { parent?: __esri.GroupLayer }).parent;
            if (parent && parent.layers) {
                parent.layers.remove(layer);
            } else {
                map.remove(layer);
            }
        } catch {
            /* noop - a layer we can't detach is still harmless, it just renders nothing */
        }
    });
}

let arcgisInitPromise: Promise<void> | null = null;

function ensureArcgisReady(apiKey?: string) {
    if (!arcgisInitPromise) {
        arcgisInitPromise = (async () => {
            // Before the SDK loads, so its canvases are created with the read-back flag.
            installArcgisCanvasReadbackFix();
            setDefaultOptions({
                url: 'https://js.arcgis.com/4.24/init.js',
            });

            const [esriConfig, IdentityManager] = (await loadModules(['esri/config', 'esri/identity/IdentityManager'])) as [
                typeof import('esri/config'),
                typeof import('esri/identity/IdentityManager')
            ];

            esriConfig.portalUrl = 'https://www.arcgis.com';

            if (apiKey && apiKey.length > 0) {
                IdentityManager.registerToken({
                    server: `${esriConfig.portalUrl}/sharing/rest`,
                    token: apiKey,
                });
            }
        })();
    }
    return arcgisInitPromise;
}

export type MapLegacyProps = {
    apiKey?: string;
    id: string;
    mapProperties?: __esri.WebMapProperties;
    viewProperties?: __esri.MapViewProperties;

    /** Optional GeoJSON sources */
    tractsUrl?: string;
    aoiUrl?: string;
    configUrl?: string;

    onSelectKuduTractID?: (id: string) => void;
    onTractSelect?: (id: string, attributes: Record<string, string | number>) => void;
    selectTractRef?: React.MutableRefObject<((tractId: string) => void) | null>;
    /** Filled with a function that zooms the map to one tract (by KuduTractID) without selecting it. */
    zoomToTractRef?: React.MutableRefObject<((tractId: string) => void) | null>;

    /** Controlled multi-select: list of selected KuduTractIDs and a setter. */
    selectedTractIds?: string[];
    onSelectedTractIdsChange?: (ids: string[]) => void;

    /** Reports the Contact Category colors the map actually resolved (project config, then
     *  auto palette, then defaults), keyed by bucket label, so other panels can match them.
     */
    onContactCategoryColors?: (colors: Record<string, number[]>) => void;

    /** Reports the Leasehold layer's WI owner colors (owner name -> RGBA, plus '__UNLEASED__' and
     *  '__OTHER__'), alpha = layer opacity.
     */
    onLeaseholdColors?: (colors: Record<string, number[]>) => void;
    /** Reports a contract layer's WI owner colors the same way, by the layer's prefix ('Brine'). */
    onContractLayerColors?: (prefix: string, colors: Record<string, number[]>) => void;

    /** Reports the Title Status layer's colors (canonical status -> RGBA, alpha = layer opacity). */
    onTitleStatusColors?: (colors: Record<string, number[]>) => void;

    /** Reports the id of the tract layer currently shown (e.g. 'tracts-contact-category'). */
    onActiveTractLayerChange?: (layerId: string | undefined) => void;

    /** Reports the ids of the tract layers the map has, once they're built (the panel's tabs follow them). */
    onTractLayersChange?: (layerIds: string[]) => void;

    /**
     * Reports that the map can't show the project: the view didn't start, or the tracts couldn't be
     * read or drawn. Given it, the page shows the problem and the map skips its own failure text.
     */
    onLoadFailed?: (step: MapLoadStep) => void;
};

type LLItemCreatedEvt = Parameters<__esri.LayerListListItemCreatedHandler>[0];

// begin component definition
const MapLegacy: React.FC<MapLegacyProps> = ({
    apiKey,
    id,
    mapProperties,
    viewProperties,
    tractsUrl,
    aoiUrl,
    configUrl,
    onSelectKuduTractID,
    onTractSelect,
    selectTractRef,
    zoomToTractRef,
    selectedTractIds: selectedTractIdsProp,
    onSelectedTractIdsChange,
    onContactCategoryColors,
    onActiveTractLayerChange,
    onTractLayersChange,
    onLeaseholdColors,
    onContractLayerColors,
    onTitleStatusColors,
    onLoadFailed,
}) => {
    // Read from ArcGIS watch callbacks / map-load code registered once at map load.
    const onActiveTractLayerChangeRef = React.useRef(onActiveTractLayerChange);
    onActiveTractLayerChangeRef.current = onActiveTractLayerChange;
    const onTractLayersChangeRef = React.useRef(onTractLayersChange);
    onTractLayersChangeRef.current = onTractLayersChange;
    const onLoadFailedRef = React.useRef(onLoadFailed);
    onLoadFailedRef.current = onLoadFailed;
    const onLeaseholdColorsRef = React.useRef(onLeaseholdColors);
    onLeaseholdColorsRef.current = onLeaseholdColors;
    const onContractLayerColorsRef = React.useRef(onContractLayerColors);
    onContractLayerColorsRef.current = onContractLayerColors;
    const onTitleStatusColorsRef = React.useRef(onTitleStatusColors);
    onTitleStatusColorsRef.current = onTitleStatusColors;
    const [mapStatus, setMapStatus] = React.useState<'loading' | 'ready' | 'failed'>('loading');
    // Tucked in until the tracts load (see readyHandle).
    const [isLegendOpen, setIsLegendOpen] = React.useState(false);
    // Whether the user has opened or shut the legend: the tracts loading doesn't open it over their choice.
    const legendToggledRef = React.useRef(false);
    const [isSelectMode, setIsSelectMode] = React.useState(false);
    const [sketchReady, setSketchReady] = React.useState(false);
    // "Tract i of n" when the clicked spot has overlapping tracts (click again to cycle).
    // Brief "i of n" tooltip at the cursor when the clicked spot has overlapping tracts; fades on its own.
    const [overlapBadge, setOverlapBadge] = React.useState<{ index: number; count: number; x: number; y: number; seq: number } | null>(null);
    const overlapBadgeTimerRef = React.useRef<number | undefined>(undefined);
    // Linger-to-peek: the tract under a still mouse (its ID) and what it's colored as on the active
    // layer, placed where the mouse rests (placeHoverTip sets its position on the element before it
    // paints). `leaving` while it fades out after the mouse moves.
    const [hoverTip, setHoverTip] = React.useState<{ tractId?: string; swatches: IHoverSwatch[]; leaving?: boolean } | null>(null);
    const hoverTipShownRef = React.useRef(false);
    const hoverTipElRef = React.useRef<HTMLDivElement>(null);
    const hoverPointRef = React.useRef({ x: 0, y: 0 });
    const placeHoverTip = React.useCallback((point: { x: number; y: number }) => {
        const el = hoverTipElRef.current;
        const node = mapNodeRef.current;
        if (!el || !node) {
            return;
        }
        const { left, top } = hoverTipPosition(
            point,
            { width: el.offsetWidth, height: el.offsetHeight },
            { width: node.clientWidth, height: node.clientHeight }
        );
        el.style.left = `${left}px`;
        el.style.top = `${top}px`;
    }, []);
    // A newly shown tip (or new content) is placed before it paints.
    React.useLayoutEffect(() => {
        if (hoverTip) {
            placeHoverTip(hoverPointRef.current);
        }
    }, [hoverTip, placeHoverTip]);
    // Per tract-layer id: how to describe a tract's color, registered as each renderer is built.
    const hoverDescribersRef = React.useRef<Record<string, HoverDescriber>>({});
    // Controlled-or-uncontrolled: if the parent passes selectedTractIds, that wins; otherwise
    // fall back to local state so MapLegacy still works in isolation (e.g. previous callers).
    const [selectedTractIdsLocal, setSelectedTractIdsLocal] = React.useState<string[]>([]);
    const selectedTractIds = selectedTractIdsProp ?? selectedTractIdsLocal;
    const setSelectedTractIds = React.useCallback(
        (ids: string[]) => {
            if (onSelectedTractIdsChange) {
                onSelectedTractIdsChange(ids);
            } else {
                setSelectedTractIdsLocal(ids);
            }
        },
        [onSelectedTractIdsChange]
    );
    const teardownRef = React.useRef<null | (() => void)>(null);
    const mapNodeRef = React.useRef<HTMLDivElement | null>(null);
    // Read inside the map-building effect without listing them as deps: callers pass
    // fresh object literals every render, which would otherwise rebuild the map endlessly.
    const mapPropertiesRef = React.useRef(mapProperties);
    mapPropertiesRef.current = mapProperties;
    const viewPropertiesRef = React.useRef(viewProperties);
    viewPropertiesRef.current = viewProperties;
    // handleLoad installs a teardown; make running it idempotent so the unmount path
    // can't double-remove widgets from an already-destroyed view.
    const runTeardown = React.useCallback(() => {
        const fn = teardownRef.current;
        teardownRef.current = null;
        try {
            fn?.();
        } catch {
            /* noop */
        }
    }, []);
    const legendContainerRef = React.useRef<HTMLDivElement | null>(null);
    const layerListRef = React.useRef<__esri.LayerList | null>(null);
    // Selection / SketchViewModel state — populated inside handleLoad once the view is ready
    const sketchVMRef = React.useRef<__esri.SketchViewModel | null>(null);
    const viewRef = React.useRef<__esri.MapView | __esri.SceneView | null>(null);
    const tractsGroupLayerRef = React.useRef<__esri.GroupLayer | null>(null);
    const selectionHighlightRef = React.useRef<__esri.Handle | null>(null);
    // Latest selection mirrored into a ref so closures inside handleLoad (e.g. layer
    // visibility watchers) always read the current value without restarting handleLoad.
    const selectedTractIdsRef = React.useRef<string[]>([]);
    selectedTractIdsRef.current = selectedTractIds;
    // Function set up inside handleLoad that recomputes the highlight from the
    // current selection and currently-visible tract sub-layer.
    const applyHighlightRef = React.useRef<(() => Promise<void>) | null>(null);
    // DOM container for the selection toolbar. We hand this node to `view.ui.add(...)`
    // so Esri stacks it under the zoom widget at the same offset Esri uses for its own
    // controls — no hardcoded pixel positioning needed. The actual button + info panel
    // are React-rendered into the container via createPortal.
    const selectionToolbarHostRef = React.useRef<HTMLDivElement | null>(null);
    if (!selectionToolbarHostRef.current && typeof document !== 'undefined') {
        selectionToolbarHostRef.current = document.createElement('div');
    }

    // Build the WebMap and MapView ourselves rather than going through react-arcgis's
    // <WebMap>. That component calls `.load()` on the basemap and *every* layer in the
    // portal item and rejects the whole pipeline if any single one fails, so one dead
    // layer means no MapView is ever constructed and the user sees nothing but
    // "The ArcGIS API failed to load." Reference General currently points three of its
    // layers at .../services/otls/MapServer/0, which UT/BEG renumbered out of existence
    // (404 "Layer not found") -- that alone took the entire map down. Here a layer that
    // won't load is dropped and logged; the map still renders.
    React.useEffect(() => {
        let cancelled = false;
        let createdView: __esri.MapView | null = null;

        (async () => {
            try {
                await ensureArcgisReady(apiKey);
                if (cancelled) {
                    return;
                }

                const [WebMap, MapView] = (await loadModules(['esri/WebMap', 'esri/views/MapView'])) as [
                    typeof import('esri/WebMap'),
                    typeof import('esri/views/MapView')
                ];
                if (cancelled) {
                    return;
                }

                const container = mapNodeRef.current;
                if (!container) {
                    return;
                }

                const map = new WebMap({ portalItem: { id }, ...mapPropertiesRef.current });
                await map.load();
                if (cancelled) {
                    return;
                }

                await loadLayersTolerantly(map);
                if (cancelled) {
                    return;
                }

                const view = new MapView({
                    container,
                    map,
                    padding: { top: 10, left: 10, right: 10, bottom: 10 },
                    // Selection draws as an outline only: the default 25% fill tints the tract and
                    // throws off the colors being read.
                    highlightOptions: { color: [0, 255, 255, 1], haloOpacity: 1, fillOpacity: 0 },
                    // North stays up at every zoom: no rotating the map by right-drag, a two-finger
                    // twist or the A/D keys.
                    rotation: 0,
                    constraints: { rotationEnabled: false },
                    ...viewPropertiesRef.current,
                });
                createdView = view;
                await view.when();
                if (cancelled) {
                    view.destroy();
                    return;
                }
                setMapStatus('ready');

                // The view is already up, so a failure while building the tract/AOI overlays
                // is not a reason to replace a working basemap with an error. react-arcgis
                // called this fire-and-forget too; the difference is it's logged rather than
                // surfacing as an unhandled rejection.
                try {
                    await handleLoad(map, view);
                } catch (err) {
                    console.log('[MapLegacy] failed while building tract layers:', err);
                    if (!cancelled) {
                        onLoadFailedRef.current?.('tract-layer');
                    }
                }
            } catch (err) {
                console.log('[MapLegacy] map failed to load:', err);
                if (!cancelled) {
                    setMapStatus('failed');
                    onLoadFailedRef.current?.('map-view');
                }
            }
        })();

        return () => {
            cancelled = true;
            runTeardown();
            try {
                createdView?.destroy();
            } catch {
                /* noop */
            }
            createdView = null;
        };
        // mapProperties/viewProperties are read through refs so that callers passing
        // fresh object literals (the usual case) don't force the whole map to rebuild.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [apiKey, id]);

    // loads all map data on screen
    const handleLoad = async (map: __esri.Map, view: __esri.MapView | __esri.SceneView) => {
        // Disable popup - attribute table handles tract info display now
        view.popup.autoOpenEnabled = false;

        // Track active highlight so we can remove it on next click
        let activeHighlight: __esri.Handle | null = null;

        // defining types imported from arcgis
        const [
            GeoJSONLayer,
            GroupLayer,
            GraphicsLayer,
            UniqueValueRenderer,
            SimpleRenderer,
            SimpleFillSymbol,
            CIMSymbol,
            LayerList,
            ScaleBar,
            SketchViewModel,
            watchUtils,
            Graphic,
        ] = (await loadModules([
            'esri/layers/GeoJSONLayer',
            'esri/layers/GroupLayer',
            'esri/layers/GraphicsLayer',
            'esri/renderers/UniqueValueRenderer',
            'esri/renderers/SimpleRenderer',
            'esri/symbols/SimpleFillSymbol',
            'esri/symbols/CIMSymbol',
            'esri/widgets/LayerList',
            'esri/widgets/ScaleBar',
            'esri/widgets/Sketch/SketchViewModel',
            'esri/core/watchUtils',
            'esri/Graphic',
        ])) as [
            typeof import('esri/layers/GeoJSONLayer'),
            typeof import('esri/layers/GroupLayer'),
            typeof import('esri/layers/GraphicsLayer'),
            typeof import('esri/renderers/UniqueValueRenderer'),
            typeof import('esri/renderers/SimpleRenderer'),
            typeof import('esri/symbols/SimpleFillSymbol'),
            typeof import('esri/symbols/CIMSymbol'),
            typeof import('esri/widgets/LayerList'),
            typeof import('esri/widgets/ScaleBar'),
            typeof import('esri/widgets/Sketch/SketchViewModel'),
            typeof import('esri/core/watchUtils'),
            typeof import('esri/Graphic')
        ];

        // url communication in console for load success / failure
        if (tractsUrl && aoiUrl && configUrl) {
            console.log('MapLegacy tractsUrl:', tractsUrl);
            console.log('MapLegacy aoiUrl:', aoiUrl);
            console.log('MapLegacy configUrl:', configUrl);
        } else if (!tractsUrl || !aoiUrl || !configUrl) {
            if (!tractsUrl) {
                console.log('[MapLegacy] No tractsUrl — skipping optional layers.');
            }

            if (!aoiUrl) {
                console.log('[MapLegacy] No aoiUrl — skipping optional layers.');
            }

            if (!configUrl) {
                console.log('[MapLegacy] No configUrl — skipping optional layers.');
            }
        } else {
            console.log('Unknown error fetching geojson urls (tracts, aoi, config)');
        }

        // NOTE: `esriConfig.apiKey` does not exist in 4.17 (it landed in 4.20), so setting it
        // here was always a no-op. Authentication is entirely the IdentityManager.registerToken
        // call in ensureArcgisReady.

        // loads per-map config from S3 blob; null when absent or invalid (callers fall back to defaults)
        const mapConfig: IMapConfig | null = await loadMapConfig(configUrl);

        // Layer-level opacity. Every symbol below is constructed fully opaque; the GeoJSONLayer's
        // `opacity` property applies the translucency once at the layer level so overlapping
        // primitives inside a feature (CIM base + hatch stripes, outlines, etc.) don't multiply
        // their alphas. Default 0.6 (40% transparent): a bit more opaque than the old baked-in 0.5
        // fill alphas, so the pale title/contact colors stay distinguishable over the basemap.
        const layerOpacity = mapConfig?.tractOpacity ?? 0.6;

        // Force every per-symbol color to alpha = 1 (fraction) / 255 (CIM). Layer opacity does the
        // rest — see `layerOpacity` above.
        const opaque = (rgba: number[]): number[] => [rgba[0], rgba[1], rgba[2], 1];
        const opaqueCim = (rgba: number[]): number[] => [rgba[0], rgba[1], rgba[2], 255];

        // TractOutlineColor replaces the default outline used everywhere. The default is opaque black:
        // the layer's transparency lightens the outline along with everything else, so it starts dark.
        const configuredOutline = normalizeRgba(mapConfig?.TractOutlineColor);
        const fillOutline = opaque(configuredOutline ?? [0, 0, 0, 1]);
        const cimBorderColor = opaqueCim(configuredOutline ?? [0, 0, 0, 1]);
        // One outline width for every tract, solid or hatched (points; 0.75 pt = 1 px). Hatched
        // tracts used to get 1.5 pt, twice the solid ones, which made them look heavier.
        const tractOutlineWidth = 0.75;

        /*
         * Hatch band width follows the zoom so bands stay in proportion to the tracts: BaseHatchingWidth
         * at zooms 13-14, doubling every two levels in (halving every two out), clamped to 2-32. It only
         * ever changes by exactly 2x, so a zoom either splits every stripe in two or keeps the stripes
         * where they were on the ground (twice as wide) instead of sliding them across the tract.
         * Keep BaseHatchingWidth a whole number that halves cleanly (8 -> 4 -> 2) for exact halving.
         *
         * The tract layers are re-symbolized when a zoom heads for a level in a different tier (see
         * hatchRefreshers). Per-tile widths through Arcade primitive overrides would avoid that, but
         * 4.24 draws overridden pattern fills at 1/128 scale with the angle read as radians.
         */
        const baseHatchWidth = mapConfig?.BaseHatchingWidth ?? 8;
        const hatchWidthForZoom = (zoom: number): number => {
            const level = Number.isFinite(zoom) && zoom >= 0 ? Math.round(zoom) : 13;
            return Math.min(32, Math.max(2, baseHatchWidth * Math.pow(2, Math.floor((level - 13) / 2))));
        };
        let hatchWidth = hatchWidthForZoom(view.zoom);
        // -30 degrees, written as 330 so it doesn't depend on how 4.24 packs a negative angle into a byte.
        const hatchRotation = 330;
        // A client band is drawn this many times wider than the others (1 = even split).
        const clientHatchWeight = 1.5;
        // ArcGIS 4.24 takes a hatch's repeat (separation) as pixels but converts its offset from
        // points to pixels (x 4/3), so offsets are written at 3/4 to land where the repeat expects.
        const hatchOffsetScale = 0.75;
        // ArcGIS 4.24 applies a hatch line's color twice (color x color / 255), which drew every band after
        // the first too dark. Each line is given the square root of its color, which that brings back.
        const hatchLineColor = (rgba: number[]): number[] => rgba.map((v, i) => (i < 3 ? Math.round(255 * Math.sqrt(v / 255)) : v));

        /*
         * Multi-value tract fill: one diagonal band per value, laid out like the desktop hatching
         * tool (DLCSymbologyTools.pyt). Shared by the Leasehold layer (bands = WI composition
         * tokens) and the Contact Category layer (bands = the tract's distinct contact categories).
         *
         * Each band is hatchWidth times its weight (1 unless given), and one repeat of the pattern is
         * exactly the sum of the bands, so they sit edge to edge. The FIRST band's color is the base
         * solid fill (no stray grey shows through); each later band is a hatch line. The first hatch
         * line sits at offset 0 and each later one steps over by the half-widths between them: 4.24
         * doesn't scale hatch offsets while tiles are stretched mid-zoom, so only offset lines slide
         * during a zoom, and a two-color tract has none. Colors come in as fractional-alpha RGBA and
         * are forced opaque — layer opacity applies the translucency once.
         */
        const buildHatchSymbol = (bandColors: number[][], bandWeights: number[] = []): __esri.CIMSymbol => {
            const bandWidths = bandColors.map((_, i) => Math.max(1, Math.round(hatchWidth * (bandWeights[i] ?? 1))));
            const repeat = bandWidths.reduce((sum, width) => sum + width, 0);

            const symbolLayers: CIMSymbolLayerType[] = [
                {
                    type: 'CIMSolidFill',
                    enable: true,
                    color: opaqueCim(bandColors[0]),
                },
            ] as unknown as CIMSymbolLayerType[];

            let bandCenter = 0;
            bandColors.slice(1).forEach((bandColor, i) => {
                const band = i + 1;
                if (i > 0) {
                    bandCenter += (bandWidths[band - 1] + bandWidths[band]) / 2;
                }
                const hatchLayer: ICIMHatchFill = {
                    type: 'CIMHatchFill',
                    enable: true,
                    lineSymbol: {
                        type: 'CIMLineSymbol',
                        symbolLayers: [
                            {
                                type: 'CIMSolidStroke',
                                enable: true,
                                capStyle: 'Butt',
                                joinStyle: 'Miter',
                                miterLimit: 10,
                                width: bandWidths[band],
                                color: opaqueCim(hatchLineColor(bandColor)),
                            } as unknown as ICIMSolidStroke,
                        ],
                    },
                    rotation: hatchRotation,
                    separation: repeat,
                    offsetY: bandCenter * hatchOffsetScale,
                    offsetX: 0,
                };

                symbolLayers.unshift(hatchLayer);
            });

            symbolLayers.unshift({
                type: 'CIMSolidStroke',
                enable: true,
                width: tractOutlineWidth,
                color: cimBorderColor,
            });

            return new CIMSymbol({
                data: {
                    type: 'CIMSymbolReference',
                    symbol: {
                        type: 'CIMPolygonSymbol',
                        symbolLayers: symbolLayers,
                    },
                },
            });
        };

        // Hides any legend row whose label carries the "|" delimiter: multi-category
        // Contact Category rows and multi-owner Leasehold (WI owner) rows. Both keep
        // their hatch symbols in the renderer (so the map still draws them) but are
        // dropped from the legend, which should only list the solid single-value entries.
        const hideCharacters = ['|']; // combined/hatched entries are delimited by |
        const hideIfPipeRow = (row: HTMLElement) => {
            const cellText = row.querySelector('.esri-legend__layer-cell--info')?.textContent || '';
            const display = hideCharacters.some((char) => cellText.includes(char)) ? 'none' : '';
            if (row.style.display !== display) {
                row.style.display = display;
            }
        };
        const hidePipeRows = () => {
            const legendRows = document.querySelectorAll('.esri-legend__layer-row') as unknown as Array<HTMLElement>;

            legendRows.forEach(hideIfPipeRow);
        };

        // The tracts file, fetched and parsed once for every collector below (each of them used to
        // fetch and parse the whole file itself). Null when there is no file or it can't be read.
        type TractFeature = { properties?: Record<string, string | number | boolean | null | undefined> | null };
        const loadTractsData = async (): Promise<{ features?: TractFeature[]; contractLayers?: unknown } | null> => {
            if (!tractsUrl) {
                return null;
            }
            try {
                const response = await fetch(tractsUrl);
                if (!response.ok) {
                    console.log('failed to fetch tracts data: ', response);
                    return null;
                }
                return await response.json();
            } catch (err) {
                console.log('failed to fetch tracts data:', err);
                return null;
            }
        };
        const tractsData = await loadTractsData();
        if (!tractsData) {
            // Nothing to build the tract layers from. The per-collector fetches this replaced all threw
            // at this point (fetch(undefined), or a failed read), so handleLoad stopped here and added
            // nothing; keep that outcome rather than add a Tracts group of layers with no data. A file
            // that was there but couldn't be read is a failure the page shows.
            if (tractsUrl) {
                onLoadFailedRef.current?.('tracts-read');
            }
            return;
        }
        const tractFeatures: TractFeature[] = tractsData.features ?? [];

        // Scan features for pipe-combined Contact_Category values and return the
        // resulting bucket combinations (e.g. "Committed | Working"). Used to
        // generate hatch-pattern symbols for multi-category tracts. Keys come from
        // contactCategoryKey, the same fold the renderer's Arcade expression runs.
        const getPipeContactBuckets = (): string[] => {
            const seen = new Set<string>();
            tractFeatures.forEach((feature) => {
                const raw = feature?.properties?.Contact_Category;
                if (typeof raw !== 'string' || !raw.includes('|')) {
                    return;
                }
                const key = contactCategoryKey(raw);
                // A key with no ' | ' folded to a single bucket (e.g. two
                // 'Committed - <sub-state>' values) and already has a solid symbol.
                if (key.includes(' | ')) {
                    seen.add(key);
                }
            });
            return Array.from(seen);
        };

        // Title Statuses — the distinct set of folded labels present in the enriched tracts
        // file. Folding here (canonicalTitleStatusValue) is what collapses the case variants
        // in HorizonExaminedHistory.OwnershipType into one row per real status, and it MUST
        // stay the same fold the renderer applies — both come from
        // DefaultTitleStatusMapColors, so they can't drift.
        //
        // 'No Title' (blank/absent status) is deliberately excluded: it is drawn by the
        // layer's defaultSymbol, so adding it here too would put two identical rows in the
        // legend. The previous code invented an 'n/a' label for these tracts, which the
        // field-based renderer could never match — a legend row no tract ever wore, sitting
        // next to the defaultSymbol's 'Other' row that they actually painted as.
        const getUniqueTitleValues = (): string[] => {
            const folded = new Set<string>();
            tractFeatures.forEach((feature) => {
                const label = canonicalTitleStatusValue(feature?.properties?.Title_Status as string | undefined);
                if (label !== NO_TITLE_LABEL) {
                    folded.add(label);
                }
            });

            const uniqueValuesArray = Array.from(folded);
            const withPipes = uniqueValuesArray.filter((item: string) => item.includes('|'));
            const withoutPipes = uniqueValuesArray.filter((item: string) => !item.includes('|'));
            const titleStatusesList = [...withoutPipes.sort(), ...withPipes];

            return titleStatusesList;
        };

        // assigning populated data accordingly
        const tractFileTileStatuses = getUniqueTitleValues();
        // The oil and gas Leasehold layer's owners and compositions. Every other contract layer runs the
        // same collection over its own fields, further down.
        const tractFileWIOwners = collectLeaseholdValues(tractFeatures, OIL_AND_GAS_LAYER_FIELDS);
        const tractFilePipeContactBuckets = getPipeContactBuckets();

        // creating the leasehold (WI owner) fill shapes
        //
        // Rendered from the server-computed WI_Composition band tokens — the single
        // source of truth shared with the tract popup. Each distinct composition is
        // one of:
        //   - a single token  -> solid fill        (e.g. "__CLIENT__", "__UNLEASED__", "Owner X")
        //   - multiple tokens  -> CIM hatch bands   (e.g. "__CLIENT__ | Owner X | __UNLEASED__")
        // Tokens map to colors: __CLIENT__ -> yellow, __UNLEASED__ -> pink/salmon,
        // a top-10 owner -> its palette color, any other owner -> grey ("Other").
        // Composition is already capped at 3 bands server-side.
        //
        // Shared by every contract layer: the Leasehold (oil and gas) layer and each other contract
        // type's layer, each passing its own collected values and config.
        const getTractLeaseholdInfo = (
            values: ILeaseholdValues,
            layerCfg: ITractLayerConfig | undefined,
            reportColors?: (colors: Record<string, number[]>, clientLabel: string) => void
        ): IUniqueValueInfoProperties[] => {
            const { top10, clientOwnerName, clientOwnerNames, compositions } = values;

            // when a config is present its assignableColorList drives auto-assignment;
            // otherwise the existing LeaseholdOwnerPalette acts as fallback rotating list.
            const fallbackPalette = LeaseholdOwnerPalette.map((p) => p.fillColor);
            const assignable = mapConfig?.assignableColorList ?? fallbackPalette;
            const autoState = createAutoAssignState();

            // Reserved band colors. Client precedence: config["__CLIENT_OWNED__"] →
            // config[clientOwnerName] → ClientOwnedColor.
            const clientOwnedColor =
                resolvePredefined('__CLIENT_OWNED__', layerCfg) ??
                (clientOwnerName ? resolvePredefined(clientOwnerName, layerCfg) : undefined) ??
                ClientOwnedColor;
            const unleasedColor = resolvePredefined('Unleased', layerCfg) ?? resolvePredefined('OPEN', layerCfg) ?? UnleasedColor;
            // Solid grey for any WI owner outside the top-10 palette ("Other").
            const otherOwnerColor: number[] = [128, 128, 128, 1];

            // Yellow / client color stays out of the rotating palette so two owners can't both render yellow.
            const skipFromAutoAssign: number[][] = [clientOwnedColor];

            // Client-acreage owners render via the __CLIENT__ band, never their own slot.
            const isClientOwnerName = (name: string): boolean => clientOwnerNames?.has(name) ?? false;

            // Owner → color map: predefined config entries first, then top-10 by frequency.
            const ownerColorMap = new Map<string, number[]>();
            const predefined = layerCfg?.predefinedColors ?? {};
            Object.keys(predefined).forEach((name) => {
                if (name === '__CLIENT_OWNED__' || name === 'Unleased' || name === 'OPEN') {
                    return;
                }
                if (isClientOwnerName(name)) {
                    return;
                }
                const c = normalizeRgba(predefined[name]);
                if (c) {
                    ownerColorMap.set(name, c);
                }
            });
            top10.forEach((name) => {
                if (ownerColorMap.has(name) || isClientOwnerName(name)) {
                    return;
                }
                const c = resolveColor(name, layerCfg, assignable, autoState, LeaseholdDefaultColor, skipFromAutoAssign);
                ownerColorMap.set(name, c);
            });

            // Token → color / label. __CLIENT__ and __UNLEASED__ are reserved; anything
            // else is a WI owner name.
            const tokenColor = (token: string): number[] => {
                if (token === '__CLIENT__') {
                    return clientOwnedColor;
                }
                if (token === '__UNLEASED__') {
                    return unleasedColor;
                }
                // A WI_Owner-derived fallback token may be a client owner's name — render it
                // as the client color rather than grey.
                if (isClientOwnerName(token)) {
                    return clientOwnedColor;
                }
                return ownerColorMap.get(token) ?? otherOwnerColor;
            };
            const tokenWeight = (token: string): number => (token === '__CLIENT__' || isClientOwnerName(token) ? clientHatchWeight : 1);
            const tokenLabel = (token: string): string => {
                if (token === '__CLIENT__') {
                    return clientOwnerName ?? 'Client Owned';
                }
                if (token === '__UNLEASED__') {
                    return 'Unleased';
                }
                return token;
            };

            // A single-owner token that is neither reserved (__CLIENT__ / __UNLEASED__),
            // a client owner, nor one of the palette-colored owners (predefined + top-10).
            // These are the "everyone else" owners: they must NOT each get their own legend
            // row. We skip emitting a per-owner uniqueValueInfo for them so the feature falls
            // through to the renderer's defaultSymbol / defaultLabel ('Other') — one grey
            // "Other" entry on the map and in the legend, instead of one row per owner.
            const isOtherOwner = (token: string): boolean =>
                token !== '__CLIENT__' && token !== '__UNLEASED__' && !isClientOwnerName(token) && !ownerColorMap.has(token);

            // WI owner name -> the color this layer draws them in, for panels that want to match
            // (client owners -> client color; '__UNLEASED__' / '__OTHER__' for open / everyone else).
            if (reportColors) {
                const withLayerAlpha = (rgba: number[]): number[] => [rgba[0], rgba[1], rgba[2], layerOpacity];
                const report: Record<string, number[]> = {
                    __CLIENT__: withLayerAlpha(clientOwnedColor),
                    __UNLEASED__: withLayerAlpha(unleasedColor),
                    __OTHER__: withLayerAlpha(otherOwnerColor),
                };
                ownerColorMap.forEach((color, name) => {
                    report[name] = withLayerAlpha(color);
                });
                (clientOwnerNames ? Array.from(clientOwnerNames) : clientOwnerName ? [clientOwnerName] : []).forEach((name) => {
                    report[name] = withLayerAlpha(clientOwnedColor);
                });
                reportColors(report, tokenLabel('__CLIENT__'));
            }

            const finalLeaseholdInfos: IUniqueValueInfoProperties[] = [];
            const seen = new Set<string>();

            // Every band token that actually appears on this map. Used below to give a legend
            // row to owners that only ever show up inside a hatch.
            const tokensInUse = new Set<string>();
            compositions.forEach((value) => {
                String(value ?? '')
                    .split('|')
                    .map((t) => t.trim())
                    .filter((t) => t.length > 0)
                    .forEach((t) => tokensInUse.add(t));
            });

            compositions.forEach((value) => {
                if (!value || seen.has(value)) {
                    return;
                }
                seen.add(value);

                const tokens = value
                    .split('|')
                    .map((t) => t.trim())
                    .filter((t) => t.length > 0);
                if (tokens.length === 0) {
                    return;
                }

                if (tokens.length === 1) {
                    // Non-top-10 / non-client owners collapse into the single default "Other"
                    // entry — skip their per-owner info (see isOtherOwner). The map still
                    // paints them via the renderer's grey defaultSymbol.
                    if (isOtherOwner(tokens[0])) {
                        return;
                    }
                    finalLeaseholdInfos.push({
                        value,
                        symbol: new SimpleFillSymbol({
                            color: opaque(tokenColor(tokens[0])),
                            outline: { color: fillOutline, width: tractOutlineWidth },
                        }),
                        label: tokenLabel(tokens[0]),
                    });
                } else {
                    // Multi-owner (hatched) entry. The renderer still needs this so the
                    // tract draws with its composite hatch symbol on the map, but we do
                    // NOT want it cluttering the legend — only solid single-owner entries
                    // should be listed. Joining the label with " | " means the existing
                    // hidePipeRows() sweep (keyed on the "|" delimiter) hides this row,
                    // mirroring how multi-category Contact Category rows are hidden.
                    finalLeaseholdInfos.push({
                        value,
                        symbol: buildHatchSymbol(tokens.map(tokenColor), tokens.map(tokenWeight)),
                        label: tokens.map(tokenLabel).join(' | '),
                    });
                }
            });

            /*
             * A palette-colored owner only got a row above if some tract is solely theirs. An
             * owner who always shares a tract — e.g. holding 1.7 of 25 net acres, so the
             * composition is "Owner | __UNLEASED__" — appears ONLY in hatch entries, and those
             * are hidden from the legend by hidePipeRows. Their color was therefore on the map
             * with nothing in the legend to explain it: project 1503.1's Charter Oak Production
             * Co. is the most frequent non-client owner, so it takes the first palette slot
             * (blue), holds 4 tracts, and every one of them is part open — blue tracts, no blue
             * key, while Continental Oil (2 tracts, one of them solely theirs) did get a row.
             *
             * So emit a solid swatch for any palette-colored owner that is on this map and
             * doesn't already have a row. It matches a feature only where a tract really is
             * solely theirs (already emitted above, hence the `seen` check); otherwise it is
             * legend-only, which is the whole point — the legend has to name every color drawn.
             * Restricted to tokensInUse so a color predefined in the map config for an owner who
             * isn't in this project can't add a row for a color nobody sees.
             */
            ownerColorMap.forEach((color, name) => {
                if (seen.has(name) || !tokensInUse.has(name)) {
                    return;
                }
                seen.add(name);
                finalLeaseholdInfos.push({
                    value: name,
                    symbol: new SimpleFillSymbol({
                        color: opaque(color),
                        outline: { color: fillOutline, width: tractOutlineWidth },
                    }),
                    label: name,
                });
            });

            // Legend order: Client Owned first, then Unleased, then the named (top-10) owners,
            // then "Other" (the renderer's defaultLabel, which ArcGIS always lists after the
            // uniqueValueInfos). Multi-owner hatch rows are hidden via hidePipeRows, so ranking
            // them last only affects the hidden tail. Array.sort is stable, so named owners keep
            // their existing (frequency-derived) relative order.
            const legendRank = (value: string | undefined): number => {
                const tokens = String(value ?? '')
                    .split('|')
                    .map((t) => t.trim())
                    .filter((t) => t.length > 0);
                if (tokens.length === 1) {
                    if (tokens[0] === '__CLIENT__') {
                        return 0;
                    }
                    if (tokens[0] === '__UNLEASED__') {
                        return 1;
                    }
                    return 2;
                }
                return 3;
            };
            finalLeaseholdInfos.sort((a, b) => legendRank(a.value) - legendRank(b.value));

            return finalLeaseholdInfos;
        };

        // creating the contact category fill shapes — one entry per whitelisted
        // canonical label, plus a hatch entry for each pipe-combined bucket combination
        // that actually appears in the data (e.g. "Committed | Working"). The renderer's
        // Arcade expression emits one of these labels per feature.
        //
        // "Other" is deliberately NOT an entry here: it is the renderer's defaultLabel, and
        // ArcGIS adds a legend row for defaultSymbol on top of every uniqueValueInfo row. An
        // explicit "Other" entry alongside that defaultLabel is what put two identical
        // "Other" rows in this legend. Rule: never give a uniqueValueInfo the same label as
        // the layer's defaultLabel.
        const getTractContactCategoryInfo = (): IUniqueValueInfoProperties[] => {
            const layerCfg: ITractLayerConfig | undefined = mapConfig?.tractLayersConfig?.MinCat;
            const assignable = mapConfig?.assignableColorList;
            const autoState = createAutoAssignState();

            const fallbackForCategory = (label: string): number[] => {
                const hardcoded = DefaultContactCategoryMapColors.find((c) => c.value === label)?.fillColor;
                return hardcoded ?? CONTACT_CATEGORY_OTHER_COLOR;
            };

            const colorFor = (label: string): number[] => {
                // "Other" bypasses resolveColor entirely. It is not a uniqueValueInfo — it is
                // the layer's defaultSymbol, which is built from CONTACT_CATEGORY_OTHER_COLOR
                // directly. Running it through the rotating palette would hand an "Other" HATCH
                // BAND a palette color while solid "Other" tracts stayed grey, i.e. one legend
                // row in two different colors depending on whether the tract had company.
                if (label === CONTACT_CATEGORY_OTHER_LABEL) {
                    return opaque(CONTACT_CATEGORY_OTHER_COLOR);
                }
                return opaque(resolveColor(label, layerCfg, assignable, autoState, fallbackForCategory(label)));
            };

            const finalContactCategories: IUniqueValueInfoProperties[] = CONTACT_CATEGORY_WHITELIST.map((label) => ({
                value: label,
                symbol: new SimpleFillSymbol({
                    color: colorFor(label),
                    outline: { color: fillOutline, width: tractOutlineWidth },
                }),
                label,
            }));

            // Colors above are resolved in whitelist order (keeps auto-assigned palettes stable);
            // the legend lists rows in uniqueValueInfos order, so sort into the DLC standard order.
            const legendRank = (value: unknown): number => {
                const i = CONTACT_CATEGORY_LEGEND_ORDER.indexOf(String(value));
                return i >= 0 ? i : CONTACT_CATEGORY_LEGEND_ORDER.length;
            };
            finalContactCategories.sort((a, b) => legendRank(a.value) - legendRank(b.value));

            // Read back from the built symbols: calling colorFor again could advance the
            // auto-assign palette and hand out different colors than the map drew. Symbols are
            // opaque; the map's translucency is the layer opacity, so report that as the alpha.
            const withCategoryLayerAlpha = (rgba: number[]): number[] => [rgba[0], rgba[1], rgba[2], layerOpacity];
            const resolvedCategoryColors: Record<string, number[]> = {
                [CONTACT_CATEGORY_OTHER_LABEL]: withCategoryLayerAlpha(CONTACT_CATEGORY_OTHER_COLOR),
            };
            finalContactCategories.forEach((info) => {
                const color = (info.symbol as __esri.SimpleFillSymbol).color;
                resolvedCategoryColors[String(info.value)] = withCategoryLayerAlpha(color.toRgba());
            });
            hoverDescribersRef.current['tracts-contact-category'] = (attributes) => contactCategorySwatches(attributes, resolvedCategoryColors);
            if (onContactCategoryColors) {
                onContactCategoryColors(resolvedCategoryColors);
            }

            // Features whose category isn't whitelisted come back from the Arcade expression as
            // CONTACT_CATEGORY_OTHER_LABEL, match no entry here, and so draw with the layer's
            // defaultSymbol — same grey, one legend row. See the note above.

            // Hatch-pattern entries for pipe-combined bucket combinations. These
            // legend rows are hidden via hidePipeRows; the renderer still needs
            // them so multi-category features draw with their composite symbol.
            // `combo` is a contactCategoryKey() result, so it is exactly what the
            // renderer's Arcade expression returns for those features.
            tractFilePipeContactBuckets.forEach((combo) => {
                const pieces = combo
                    .split('|')
                    .map((v) => v.trim())
                    .filter((v) => v.length > 0);

                finalContactCategories.push({
                    value: combo,
                    symbol: buildHatchSymbol(pieces.map(colorFor)),
                    label: combo,
                });
            });

            return finalContactCategories;
        };

        // creating the title statuses fill shapes
        const getTractTitleStatusInfo = (): IUniqueValueInfoProperties[] => {
            const layerCfg: ITractLayerConfig | undefined = mapConfig?.tractLayersConfig?.TypeCheck;
            const assignable = mapConfig?.assignableColorList;
            const autoState = createAutoAssignState();

            // One of the seven valid statuses gets its business-assigned color. Anything else
            // is a value that shouldn't exist (e.g. 'Tax Records') — it still needs to be
            // visible and distinct so it can be found and fixed, so it falls through to the
            // rotating palette rather than being hidden in grey.
            const fallbackForStatus = (s: string): number[] => titleStatusColor(s) ?? [80, 80, 80, 0.2];

            const finalTitleStatuses: IUniqueValueInfoProperties[] = [];

            tractFileTileStatuses.forEach((status) => {
                const color = opaque(resolveColor(status, layerCfg, assignable, autoState, fallbackForStatus(status)));
                finalTitleStatuses.push({
                    value: status,
                    symbol: new SimpleFillSymbol({
                        color,
                        outline: { color: fillOutline, width: tractOutlineWidth },
                    }),
                    label: status,
                });
            });
            // Status -> the color drawn (alpha = layer opacity) so the Tract Info tab can match it.
            const report: Record<string, number[]> = {};
            finalTitleStatuses.forEach((info) => {
                const c = (info.symbol as __esri.SimpleFillSymbol).color.toRgba();
                report[String(info.value)] = [c[0], c[1], c[2], layerOpacity];
            });
            const noTitleRgba = [NO_TITLE_COLOR[0], NO_TITLE_COLOR[1], NO_TITLE_COLOR[2], layerOpacity];
            hoverDescribersRef.current['tracts-title-status'] = (attributes) => titleStatusSwatches(attributes, report, noTitleRgba);
            onTitleStatusColorsRef.current?.(report);
            // Colors were resolved above in data order (keeps auto-assigned palettes stable); the
            // legend lists uniqueValueInfos in array order, so sort most-comprehensive first.
            finalTitleStatuses.sort((a, b) => titleStatusLegendRank(String(a.value)) - titleStatusLegendRank(String(b.value)));
            return finalTitleStatuses;
        };

        // Tract-number labels, reused across every tract layer. Arcade extracts just the tract portion
        // of KuduTractID (e.g. "1421.1.1.9-1" → "9"). Hidden at project-overview zoom, they come in
        // past ~1:50,000 (zoom 14, about parcel-readable) light and small, so they don't crowd the
        // colors, and stay that way until a section about fills the map: past ~1:12,500 (zoom 16 on)
        // they switch to darker, a little bigger and bold.
        const TRACT_LABEL_COME_IN_SCALE = 50000;
        const TRACT_LABEL_CLOSE_UP_SCALE = 12500;
        const tractLabelExpression = {
            // Null-safe. An empty/malformed KuduTractID -- or one that ArcGIS drops during
            // GeoJSON field-type inference (see the "fields ... were dropped" warning) --
            // must NOT throw. A throwing label expression floods the console with
            // bad-arcade-expression and, in this SDK build, aborts tile rendering for the
            // affected features, so tracts come back white. Return '' instead of erroring.
            expression: `
                var kt = $feature.KuduTractID;
                if (IsEmpty(kt)) { return ''; }
                var head = Split(kt, '-')[0];
                var parts = Split(head, '.');
                if (Count(parts) > 3) { return parts[3]; }
                return '';
            `,
        };
        // One label style for a scale range (minScale the farthest out it shows, maxScale the closest).
        const tractLabel = (
            minScale: number,
            maxScale: number,
            color: number[],
            size: number,
            weight: 'normal' | 'bold',
            haloSize: number
        ) =>
            (({
                labelExpressionInfo: tractLabelExpression,
                symbol: {
                    type: 'text',
                    color,
                    haloColor: [255, 255, 255, 0.9],
                    haloSize,
                    font: { family: 'Arial', size, weight },
                },
                labelPlacement: 'always-horizontal',
                minScale,
                maxScale,
            } as unknown) as __esri.LabelClassProperties);
        const tractLabelClasses = [
            // Coming in: light and small.
            tractLabel(TRACT_LABEL_COME_IN_SCALE, TRACT_LABEL_CLOSE_UP_SCALE, [95, 99, 104, 1], 8, 'normal', 1),
            // Zoomed in further: darker, a little bigger and bold.
            tractLabel(TRACT_LABEL_CLOSE_UP_SCALE, 0, [20, 20, 20, 1], 10, 'bold', 1.5),
        ];

        // generating the area of interest layer from the provided aoi file (if it exists)
        const aoiLayer = aoiUrl
            ? new GeoJSONLayer({
                  url: aoiUrl,
                  title: 'Areas of Interest',
                  outFields: ['*'],
                  // No fill, a heavy dark red outline at 60% opacity (the tracts' default too), so the
                  // AOI reads over the tract fills without hiding their edges.
                  opacity: 0.6,
                  renderer: new SimpleRenderer({
                      symbol: new SimpleFillSymbol({
                          style: 'solid',
                          color: [0, 0, 0, 0],
                          outline: { color: [139, 0, 0, 1], width: 4 },
                      }),
                  }),
              })
            : undefined;

        // Leasehold layer value expression. Only reference the WI_Composition field when the
        // data actually contains it; otherwise Arcade raises a field-not-found error and the
        // tract does not render. Falls back to WI_Owner / Is_Client_Owned (always present).
        const leaseholdValueExpression = contractLayerValueExpression(OIL_AND_GAS_LAYER_FIELDS, tractFileWIOwners.hasRawComposition);

        // Every contract type the file carries besides oil and gas (brine, solar, right of way, ...)
        // gets its own layer, drawn exactly like the Leasehold layer from its own {prefix}_* fields.
        // Nothing is combined across types. Owner colors predefined for the Leasehold layer carry
        // over unless the config gives the type its own block (tractLayersConfig.Brine, ...), so a
        // company keeps its color from one contract layer to the next.
        // Every layer drawn with hatch symbols registers how to rebuild its classes, so the bands can
        // be re-sized when a zoom lands in another width tier (see hatchWidthForZoom). Only the visible
        // layer is re-symbolized then; a hidden one catches up when it is shown.
        type HatchLayer = { layer: __esri.GeoJSONLayer; width: number; refresh: () => void };
        const hatchLayers: HatchLayer[] = [];
        const hatchVisibilityHandles: __esri.WatchHandle[] = [];
        const withHatchRefresh = (layer: __esri.GeoJSONLayer, buildInfos: () => IUniqueValueInfoProperties[]): __esri.GeoJSONLayer => {
            const entry: HatchLayer = {
                layer,
                width: hatchWidth,
                refresh: () => {
                    const renderer = (layer.renderer as __esri.UniqueValueRenderer).clone();
                    renderer.uniqueValueInfos = buildInfos() as unknown as __esri.UniqueValueInfo[];
                    layer.renderer = renderer;
                    entry.width = hatchWidth;
                },
            };
            hatchLayers.push(entry);
            hatchVisibilityHandles.push(
                layer.watch('visible', (visible: boolean) => {
                    if (visible && entry.width !== hatchWidth) {
                        entry.refresh();
                    }
                })
            );
            return layer;
        };
        const setHatchWidth = (width: number) => {
            hatchWidth = width;
            hatchLayers.forEach((entry) => {
                if (entry.layer.visible && entry.width !== width) {
                    entry.refresh();
                }
            });
        };

        const contractLayers = readContractLayerManifest(tractsData).map((entry) => {
            const fields = contractLayerFields(entry.prefix);
            const values = collectLeaseholdValues(tractFeatures, fields);
            const typeCfg = mapConfig?.tractLayersConfig?.[entry.prefix];
            const layerCfg: ITractLayerConfig = {
                alias: typeCfg?.alias,
                predefinedColors: typeCfg?.predefinedColors ?? mapConfig?.tractLayersConfig?.Lessee?.predefinedColors,
            };
            const title = typeCfg?.alias ?? contractLayerTitle(entry);

            const layer = new GeoJSONLayer({
                id: `tracts-contract-${entry.prefix}`,
                url: tractsUrl,
                title,
                // The Leasehold layer stays the one the map opens on.
                visible: false,
                outFields: ['*'],
                opacity: layerOpacity,
                labelingInfo: tractLabelClasses,
                renderer: new UniqueValueRenderer({
                    valueExpression: contractLayerValueExpression(fields, values.hasRawComposition),
                    valueExpressionTitle: typeCfg?.alias ?? 'WI Owner',
                    uniqueValueInfos: getTractLeaseholdInfo(values, layerCfg, (colors, clientLabel) => {
                        hoverDescribersRef.current[`tracts-contract-${entry.prefix}`] = (attributes) =>
                            compositionSwatches(attributes, fields, colors, clientLabel);
                        onContractLayerColorsRef.current?.(entry.prefix, colors);
                    }),
                    defaultSymbol: new SimpleFillSymbol({
                        color: opaque([128, 128, 128, 1]),
                        outline: { color: fillOutline, width: tractOutlineWidth },
                    }),
                    defaultLabel: 'Other',
                }),
            });
            return withHatchRefresh(layer, () => getTractLeaseholdInfo(values, layerCfg));
        });

        // group layer allowing viewing of contact vs. tract (radio button selection in legend)
        const tractsGroupLayer = new GroupLayer({
            id: 'tracts-group',
            title: 'Tracts',
            visibilityMode: 'exclusive',
            layers: [
                withHatchRefresh(
                    new GeoJSONLayer({
                        id: 'tracts-contact-category',
                        url: tractsUrl,
                        title: mapConfig?.tractLayersConfig?.MinCat?.alias ?? 'Contact Category',
                        outFields: ['*'],
                        opacity: layerOpacity,
                        labelingInfo: tractLabelClasses,
                        renderer: new UniqueValueRenderer({
                            // Buckets each pipe piece of Contact_Category to a whitelisted canonical
                            // label (or "Other"), dedupes, and returns either the single bucket or a
                            // pipe-joined combo ("Committed | Working") that matches a pre-built
                            // hatch entry in uniqueValueInfos. Generated from the same whitelist as
                            // contactCategoryKey so the renderer and the legend cannot drift.
                            valueExpression: contactCategoryValueExpression(),
                            valueExpressionTitle: mapConfig?.tractLayersConfig?.MinCat?.alias ?? 'Contact Category',
                            uniqueValueInfos: getTractContactCategoryInfo(),
                            defaultSymbol: new SimpleFillSymbol({
                                color: opaque(CONTACT_CATEGORY_OTHER_COLOR),
                                outline: { color: fillOutline, width: tractOutlineWidth },
                            }),
                            defaultLabel: CONTACT_CATEGORY_OTHER_LABEL,
                        }),
                    }),
                    getTractContactCategoryInfo
                ),
                new GeoJSONLayer({
                    id: 'tracts-title-status',
                    url: tractsUrl,
                    title: mapConfig?.tractLayersConfig?.TypeCheck?.alias ?? 'Title Status',
                    outFields: ['*'],
                    opacity: layerOpacity,
                    labelingInfo: tractLabelClasses,
                    renderer: new UniqueValueRenderer({
                        // Folds case/wording variants of Title_Status to the seven valid
                        // statuses, so the MAP agrees with the legend. Was `field:
                        // 'Title_Status'`, a raw case-sensitive match that made 'cursory',
                        // 'Cursory' and 'CURSORY' three separate classes in three colors.
                        valueExpression: titleStatusValueExpression(),
                        valueExpressionTitle: mapConfig?.tractLayersConfig?.TypeCheck?.alias ?? 'Title Status',
                        uniqueValueInfos: getTractTitleStatusInfo(),
                        // Tracts with no examination history. The expression returns
                        // NO_TITLE_LABEL for them and getUniqueTitleValues omits it, so they
                        // land here — exactly one grey legend row, correctly named. This slot
                        // used to be labelled 'Other', which said nothing about why a tract
                        // was in it.
                        defaultSymbol: new SimpleFillSymbol({
                            color: opaque(NO_TITLE_COLOR),
                            outline: { color: fillOutline, width: tractOutlineWidth },
                        }),
                        defaultLabel: NO_TITLE_LABEL,
                    }),
                }),
                // A group lists its last layer first, so reversed the contract layers read in manifest
                // order (Brine, Solar, ...) directly under Leasehold.
                ...contractLayers.slice().reverse(),
                withHatchRefresh(
                    new GeoJSONLayer({
                        id: 'tracts-leasehold',
                        url: tractsUrl,
                        title: mapConfig?.tractLayersConfig?.Lessee?.alias ?? LEASEHOLD_LAYER_TITLE,
                        outFields: ['*'],
                        opacity: layerOpacity,
                        labelingInfo: tractLabelClasses,
                        renderer: new UniqueValueRenderer({
                            // Effective WI composition: prefer the server-computed WI_Composition
                            // token; when absent (older enriched files) derive an equivalent from the
                            // always-present WI_Owner / Is_Client_Owned so the tract still renders.
                            // MUST stay in sync with the collection in getUniqueWIOwnerValues.
                            valueExpression: leaseholdValueExpression,
                            valueExpressionTitle: mapConfig?.tractLayersConfig?.Lessee?.alias ?? 'WI Owner',
                            uniqueValueInfos: getTractLeaseholdInfo(tractFileWIOwners, mapConfig?.tractLayersConfig?.Lessee, (colors, clientLabel) => {
                                hoverDescribersRef.current['tracts-leasehold'] = (attributes) =>
                                    compositionSwatches(attributes, OIL_AND_GAS_LAYER_FIELDS, colors, clientLabel);
                                onLeaseholdColorsRef.current?.(colors);
                            }),
                            // Catches every non-top-10 / non-client WI owner (whose per-owner
                            // entries getTractLeaseholdInfo deliberately omits) as one grey
                            // "Other" bucket on the map and in the legend. Grey matches the
                            // otherOwnerColor previously used for these owners' individual rows.
                            defaultSymbol: new SimpleFillSymbol({
                                color: opaque([128, 128, 128, 1]),
                                outline: { color: fillOutline, width: tractOutlineWidth },
                            }),
                            defaultLabel: 'Other',
                        }),
                    }),
                    // Re-sizes rebuild the same colors, so they aren't re-reported to the side panel.
                    () => getTractLeaseholdInfo(tractFileWIOwners, mapConfig?.tractLayersConfig?.Lessee)
                ),
                // Layers the project's config turns off (GIS portal, Map Layers) never join the group.
            ].filter((layer) => {
                const key = tractLayerConfigKey(layer.id);
                return !key || isTractLayerEnabled(mapConfig, key);
            }),
        });

        // The map opens on the legend's top layer: Leasehold, or whichever enabled layer sits highest
        // on a project that turned Leasehold off.
        const openingLayer = tractsGroupLayer.layers.getItemAt(tractsGroupLayer.layers.length - 1);
        tractsGroupLayer.layers.forEach((layer) => {
            layer.visible = layer === openingLayer;
        });

        // A layer the config leaves out never draws, so it takes no hatch re-sizes.
        for (let i = hatchLayers.length - 1; i >= 0; i--) {
            if (!tractsGroupLayer.layers.includes(hatchLayers[i].layer)) {
                hatchLayers.splice(i, 1);
            }
        }

        // The tracts and the AOI go under the web map's own layers (the USA reference boundaries and
        // the survey grids), just above the basemap, so those lines draw over the tract fills; the AOI
        // sits above the tracts, its outline over their fills (it has no fill of its own). Clicks and
        // hovers hit-test only the tract layers, so the layers on top don't get in the way.
        const layersToAdd = [tractsGroupLayer, aoiLayer].filter(Boolean) as __esri.Layer[];

        if (layersToAdd.length) {
            map.addMany(layersToAdd, 0);
        }

        // A tract layer that can't load would leave the map empty, so the page says so instead. Only
        // the one shown at load is watched: hidden layers may never load, and one failing contract
        // layer shouldn't block the page.
        tractsGroupLayer?.layers
            .find((layer) => layer.visible)
            ?.when()
            .catch(() => onLoadFailedRef.current?.('tract-layer'));

        // Selected tracts get a cyan border on a layer of their own above the tracts. Nothing is drawn
        // over the tract itself, so its color reads true. The clicked tract and the rectangle selection
        // keep their own geometries; the layer is redrawn from both.
        const selectionOutlineLayer = new GraphicsLayer({ id: 'tract-selection-outline', listMode: 'hide' });
        map.add(selectionOutlineLayer);
        const selectionLine = new SimpleFillSymbol({ color: [0, 0, 0, 0], outline: { color: [0, 180, 255, 1], width: 2 } });
        let clickedGeometries: __esri.Geometry[] = [];
        let selectedGeometries: __esri.Geometry[] = [];
        const redrawSelectionOutline = () => {
            selectionOutlineLayer.removeAll();
            selectionOutlineLayer.addMany(
                [...selectedGeometries, ...clickedGeometries].map((geometry) => new Graphic({ geometry, symbol: selectionLine }))
            );
        };

        // The selected tracts themselves lift off the map: a soft drop shadow on the tract in the visible
        // layer (a feature effect, so the tract's own fill and outline cast it). Other tracts are left
        // alone, so no color shifts. Follows layer switches through applyHighlight.
        const selectedTractEffect = 'drop-shadow(0px, 1px, 3px, rgba(0, 0, 0, 0.5))';
        let clickedTractId: string | null = null;
        let effectLayerView: __esri.LayerView | null = null;
        let effectSeq = 0;
        const setFeatureEffect = (layerView: __esri.LayerView, effect: object | null) => {
            (layerView as unknown as { featureEffect: object | null }).featureEffect = effect;
        };
        const refreshSelectedTractEffect = async () => {
            const seq = ++effectSeq;
            const ids = [...selectedTractIdsRef.current, ...(clickedTractId ? [clickedTractId] : [])];
            const visibleLayer = tractsGroupLayer.layers.find((l) => l.visible);
            if (effectLayerView) {
                setFeatureEffect(effectLayerView, null);
                effectLayerView = null;
            }
            if (!visibleLayer || !ids.length) {
                return;
            }
            try {
                const layerView = await view.whenLayerView(visibleLayer);
                if (seq !== effectSeq) {
                    return;
                }
                const list = ids.map((kuduId) => `'${kuduId.replace(/'/g, `''`)}'`).join(',');
                // excludedLabelsVisible: a feature effect otherwise hides every other tract's label.
                setFeatureEffect(layerView, {
                    filter: { where: `KuduTractID IN (${list})` },
                    includedEffect: selectedTractEffect,
                    excludedLabelsVisible: true,
                });
                effectLayerView = layerView;
            } catch {
                /* no layer view yet; the next selection or layer switch retries */
            }
        };
        // The clicked tract's full geometry, from the layer: a hit-test graphic can carry a generalized
        // display copy. A newer click wins over a query still in flight.
        let clickOutlineSeq = 0;
        const outlineClickedTract = async (layer: __esri.FeatureLayer | null, graphic: __esri.Graphic | null) => {
            const seq = ++clickOutlineSeq;
            let geometry = graphic?.geometry ?? null;
            const kuduId = graphic?.attributes?.KuduTractID;
            if (layer && typeof kuduId === 'string' && kuduId) {
                try {
                    const result = await layer.queryFeatures({
                        where: `KuduTractID = '${kuduId.replace(/'/g, `''`)}'`,
                        outFields: ['KuduTractID'],
                        returnGeometry: true,
                    });
                    geometry = result.features[0]?.geometry ?? geometry;
                } catch {
                    /* keep the hit-test geometry */
                }
            }
            if (seq === clickOutlineSeq) {
                clickedGeometries = geometry ? [geometry] : [];
                redrawSelectionOutline();
            }
        };

        // Re-size the hatch bands as soon as a zoom lands in a level from another width tier. Starts
        // once the map has made its first zoom to the tracts (see readyHandle).
        let hatchZoomTracking = false;
        const hatchZoomHandle = view.watch('stationary', (stationary: boolean) => {
            if (stationary && hatchZoomTracking) {
                setHatchWidth(hatchWidthForZoom(view.zoom));
            }
        });

        // creating scalebar — moved to bottom-left so it stays visible behind the legend drawer
        const scalebar = new ScaleBar({ view, unit: 'non-metric' });
        view.ui.add(scalebar, 'bottom-left');

        // Mount the selection toolbar host into Esri's top-left UI corner so it stacks
        // under the zoom widget. The React portal below renders the button + info panel
        // into this node.
        if (selectionToolbarHostRef.current) {
            view.ui.add(selectionToolbarHostRef.current, 'top-left');
        }

        // === Rectangle multi-select ============================================
        // A transient graphics layer holds the rubber-band rectangle while the user
        // is dragging; we remove the graphic after we extract its geometry so the
        // rectangle doesn't linger on the map.
        const sketchLayer = new GraphicsLayer({ listMode: 'hide' });
        map.add(sketchLayer);

        const sketchVM = new SketchViewModel({
            view,
            layer: sketchLayer,
            // Rubber-band style — subtle blue dashed outline, faint blue fill.
            polygonSymbol: {
                type: 'simple-fill',
                color: [50, 100, 200, 0.1],
                outline: { color: [50, 100, 200, 1], width: 1.5, style: 'dash' },
            } as unknown as __esri.SimpleFillSymbolProperties,
        });

        sketchVM.on('create', async (event) => {
            if (event.state !== 'complete') {
                return;
            }
            const geometry = event.graphic?.geometry;
            // Remove the rubber-band so only the highlights remain.
            try {
                sketchLayer.remove(event.graphic);
            } catch {
                /* noop */
            }
            if (!geometry) {
                setIsSelectMode(false);
                return;
            }

            // Query the currently visible tract sub-layer (they share the same features
            // by KuduTractID, so any visible layer answers the question).
            const visibleLayer = tractsGroupLayer.layers.find((l) => l.visible) as __esri.GeoJSONLayer | undefined;
            if (!visibleLayer) {
                setIsSelectMode(false);
                return;
            }

            try {
                const result = await visibleLayer.queryFeatures({
                    geometry,
                    spatialRelationship: 'intersects',
                    outFields: ['KuduTractID'],
                    returnGeometry: false,
                });
                const ids = result.features
                    .map((f) => f.attributes?.KuduTractID)
                    .filter((kuduId: unknown): kuduId is string => typeof kuduId === 'string' && kuduId.length > 0);
                // Phase 1: replace selection on each draw. Shift/Ctrl modifiers can layer on later.
                setSelectedTractIds(Array.from(new Set(ids)));
            } catch (err) {
                console.log('[Select] queryFeatures failed:', err);
            } finally {
                setIsSelectMode(false);
            }
        });

        viewRef.current = view;
        tractsGroupLayerRef.current = tractsGroupLayer;
        sketchVMRef.current = sketchVM;

        // Recompute the highlight from the current selection + currently visible tract layer.
        // Called by the selection useEffect (selection changed) and by per-layer visibility
        // watchers (legend radio toggled).
        const applyHighlight = async () => {
            if (selectionHighlightRef.current) {
                selectionHighlightRef.current.remove();
                selectionHighlightRef.current = null;
            }
            void refreshSelectedTractEffect();
            const ids = selectedTractIdsRef.current;
            if (!ids.length) {
                selectedGeometries = [];
                redrawSelectionOutline();
                return;
            }
            const visibleLayer = tractsGroupLayer.layers.find((l) => l.visible) as __esri.GeoJSONLayer | undefined;
            if (!visibleLayer) {
                return;
            }
            try {
                const layerView = (await view.whenLayerView(visibleLayer)) as __esri.FeatureLayerView;
                const escapedIds = ids.map((kuduId) => `'${kuduId.replace(/'/g, `''`)}'`).join(',');
                const result = await visibleLayer.queryFeatures({
                    where: `KuduTractID IN (${escapedIds})`,
                    returnGeometry: true,
                });
                selectionHighlightRef.current = layerView.highlight(result.features);
                selectedGeometries = result.features.map((feature) => feature.geometry).filter(Boolean);
                redrawSelectionOutline();
            } catch (err) {
                console.log('[Select] highlight failed:', err);
            }
        };
        applyHighlightRef.current = applyHighlight;

        // The clicked tract stays selected across layer switches: its highlight moves to the layer now
        // shown (the border layer and the drop shadow follow on their own).
        const highlightClickedTractIn = async (layer: __esri.Layer) => {
            const kuduId = clickedTractId;
            if (!kuduId) {
                return;
            }
            try {
                const layerView = (await view.whenLayerView(layer)) as __esri.FeatureLayerView;
                const objectIds = await (layer as __esri.FeatureLayer).queryObjectIds({ where: `KuduTractID = '${kuduId.replace(/'/g, `''`)}'` });
                if (kuduId !== clickedTractId || !layer.visible) {
                    return;
                }
                activeHighlight?.remove();
                activeHighlight = layerView.highlight(objectIds);
            } catch (err) {
                console.log('Could not carry the selection to this layer:', err);
            }
        };

        // Re-apply the highlight whenever the user switches the visible tract layer
        // (Leasehold ↔ Title Status ↔ Contact Category) so the selection follows.
        tractsGroupLayer.layers.forEach((layer) => {
            layer.watch('visible', () => {
                if (layer.visible) {
                    applyHighlightRef.current?.();
                    void highlightClickedTractIn(layer);
                    onActiveTractLayerChangeRef.current?.(layer.id);
                }
            });
        });
        const initiallyVisible = tractsGroupLayer.layers.find((l) => l.visible);
        onActiveTractLayerChangeRef.current?.(initiallyVisible ? initiallyVisible.id : undefined);
        onTractLayersChangeRef.current?.(tractsGroupLayer.layers.toArray().map((l) => l.id));

        setSketchReady(true);
        // =======================================================================

        // Create the LayerList widget and mount it directly into our React-owned
        // drawer container — no ArcGIS Expand wrapper. The drawer chrome (slide
        // animation, full-height layout, toggle button) is plain React/CSS.
        const layerList = new LayerList({
            view,
            container: legendContainerRef.current ?? undefined,
            listItemCreatedFunction: (event: LLItemCreatedEvt) => {
                const item = event.item;
                if (item.layer.type === 'group') {
                    if (item.layer.id === 'tracts-group') {
                        item.open = true;
                    }
                    return;
                }
                // All tract sub-layers get a legend panel; we toggle the panel's `open`
                // state in reaction to visibility so only the currently selected layer
                // shows its swatches. `panel.open` is a reactive Accessor property — it
                // re-renders the LayerList automatically. Reassigning the whole `panel`
                // object does not, which is why a previous null-the-panel approach
                // didn't update the UI.
                item.panel = { content: 'legend', open: !!item.layer.visible } as __esri.ListItemPanel;
                item.layer.watch('visible', (visible: boolean) => {
                    if (item.panel) {
                        item.panel.open = !!visible;
                    }
                });
            },
        });
        layerListRef.current = layerList;

        // Hide the "|"-delimited (hatched) rows from the legend. The ArcGIS LayerList
        // renders — and re-renders on every layer switch — asynchronously, so we must
        // NOT depend on '.esri-layer-list' existing at an arbitrary 500ms: in a fresh
        // production build it isn't there yet, the observer never attaches, and the
        // hatched rows leak back into the legend. Instead observe our own drawer
        // container (always present — it's the LayerList's `container`) and sweep on
        // every mutation, plus once up front.
        // "Loading…" after a tract layer's title while that layer is drawing (e.g. right after it's
        // picked). LayerList has no slot for custom row content, so — like hidePipeRows — tag the
        // row by its title and let CSS add the text; re-applied whenever the list re-renders.
        const updatingLayerTitles = new Set<string>();
        const markUpdatingRows = () => {
            const container = legendContainerRef.current;
            if (!container) {
                return;
            }
            container.querySelectorAll('.esri-layer-list__item-title').forEach((titleEl) => {
                const row = titleEl.closest('.esri-layer-list__item-container');
                if (!row) {
                    return;
                }
                const want = updatingLayerTitles.has((titleEl.textContent || '').trim());
                if (row.classList.contains('lh-layer-updating') !== want) {
                    row.classList.toggle('lh-layer-updating', want);
                }
            });
        };
        tractsGroupLayer.layers.forEach((layer) => {
            const setLoading = (loading: boolean) => {
                if (loading) {
                    updatingLayerTitles.add(layer.title);
                } else {
                    updatingLayerTitles.delete(layer.title);
                }
                markUpdatingRows();
            };
            view.whenLayerView(layer)
                .then((lv) => {
                    // Picked in the legend: show "Loading…" at once, clear when drawing settles
                    // (or shortly, if the view had nothing to redraw).
                    layer.watch('visible', (visible: boolean) => {
                        setLoading(visible);
                        if (visible) {
                            window.setTimeout(() => !lv.updating && setLoading(false), 400);
                        }
                    });
                    lv.watch('updating', (updating: boolean) => setLoading(updating && layer.visible));
                })
                .catch(() => {
                    /* layer failed to load; nothing to indicate */
                });
        });

        const sweepLegend = () => {
            hidePipeRows();
            markUpdatingRows();
        };
        sweepLegend();
        // Coalesce: ArcGIS fires many class mutations per frame while a layer draws, and a sweep
        // walks every legend row (Contact Category has hundreds), so sweep at most once per frame.
        let sweepFrame = 0;
        const scheduleSweep = () => {
            if (!sweepFrame) {
                sweepFrame = window.requestAnimationFrame(() => {
                    sweepFrame = 0;
                    sweepLegend();
                });
            }
        };
        // The legend re-renders inside an animation frame (a zoom into another hatch tier re-symbolizes
        // the layer, for one), so a sweep scheduled from there runs a frame late and the browser paints
        // the new pipe rows once first: a flash of the hidden hatch rows. Rows just added are hidden on
        // the spot, before that paint; only those, so it stays cheap. The full sweep still runs once a
        // frame.
        const hideAddedPipeRows = (records: MutationRecord[]) => {
            records.forEach((record) => {
                Array.from(record.addedNodes).forEach((node) => {
                    const element = node instanceof Element ? node : node.parentElement;
                    const row = element?.closest('.esri-legend__layer-row') as HTMLElement | null | undefined;
                    if (row) {
                        hideIfPipeRow(row);
                    } else if (element) {
                        (element.querySelectorAll('.esri-legend__layer-row') as unknown as Array<HTMLElement>).forEach(hideIfPipeRow);
                    }
                });
            });
        };
        const legendContainer = legendContainerRef.current;
        if (legendContainer) {
            const observer = new MutationObserver((records) => {
                hideAddedPipeRows(records);
                scheduleSweep();
            });
            // attributes: LayerList re-render resets row classes, which would drop the loading mark.
            observer.observe(legendContainer, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
        }

        // Shared tract selection logic used by both click handler and programmatic selection
        type TractAttrs = Partial<{
            GlobalID: string;
            globalid: string;
            GLOBALID: string;
            globalId: string;
            KuduTractID: string;
            KuduTractId: string;
            KUDUTractId: string;
            KUDUTRACTID: string;
            kudutractid: string;
        }>;

        // Last click's overlap stack, so a repeat click on the same spot advances to the next tract.
        let overlapCycle: { key: string; x: number; y: number; index: number } | null = null;

        const handleTractHit = async (screenPoint: { x: number; y: number }) => {
            if (!tractsGroupLayer) {
                return undefined;
            }

            // Every tract layer, contract layers included: only the visible one draws, so only it can be hit.
            const hit = await view.hitTest(screenPoint, { include: tractsGroupLayer.layers.toArray() });
            const first = (hit?.results?.[0] as __esri.HitTestResult['results'][number]) || undefined;

            // hitTest (4.17) returns only the topmost feature per layer, so overlapping tracts
            // underneath are unreachable. Query every feature under the point on the hit layer,
            // topmost first, and let repeated clicks on the same spot cycle through the tracts.
            // A tract's horizons are separate features sharing one polygon, so they count once;
            // the hit (topmost, drawn) feature speaks for its own tract.
            let graphic = first?.graphic;
            const layer = graphic?.layer as __esri.FeatureLayer;
            if (graphic && layer) {
                try {
                    const lv = (await view.whenLayerView(layer)) as __esri.FeatureLayerView;
                    const q = lv.createQuery();
                    q.geometry = view.toMap(screenPoint);
                    q.spatialRelationship = 'intersects';
                    q.outFields = ['*'];
                    q.returnGeometry = false;
                    const under = (await lv.queryFeatures(q)).features || [];

                    const oidField = layer.objectIdField;
                    const topOid = graphic.attributes?.[oidField];
                    const stack = firstPerTract(
                        [graphic, ...under.filter((g) => g.attributes?.[oidField] !== topOid)],
                        (g) => g.attributes?.KuduTractID ?? g.attributes?.KuduTractId
                    );
                    const stackKey = stack.map((g) => g.attributes?.[oidField]).join(',');

                    const prev = overlapCycle;
                    const sameSpot = prev && prev.key === stackKey && Math.abs(prev.x - screenPoint.x) <= 6 && Math.abs(prev.y - screenPoint.y) <= 6;
                    const index = sameSpot && prev ? (prev.index + 1) % stack.length : 0;
                    overlapCycle = { key: stackKey, x: screenPoint.x, y: screenPoint.y, index };

                    graphic = stack[index];
                    window.clearTimeout(overlapBadgeTimerRef.current);
                    if (stack.length > 1) {
                        setOverlapBadge({ index: index + 1, count: stack.length, x: screenPoint.x, y: screenPoint.y, seq: Date.now() });
                        overlapBadgeTimerRef.current = window.setTimeout(() => setOverlapBadge(null), 1600);
                    } else {
                        setOverlapBadge(null);
                    }
                } catch (err) {
                    console.log('Could not query overlapping tracts:', err);
                    setOverlapBadge(null);
                }
            } else {
                overlapCycle = null;
                setOverlapBadge(null);
            }

            const attrs = graphic?.attributes as TractAttrs | undefined;
            const allAttrs = graphic?.attributes as Record<string, string | number> | undefined;

            // Highlight the tract
            if (activeHighlight) {
                activeHighlight.remove();
                activeHighlight = null;
            }
            if (graphic && layer) {
                try {
                    const layerView = (await view.whenLayerView(layer)) as __esri.FeatureLayerView;
                    activeHighlight = layerView.highlight(graphic);
                } catch (err) {
                    console.log('Could not highlight tract:', err);
                }
            }
            void outlineClickedTract(graphic && layer ? layer : null, graphic ?? null);
            const clickedKuduId = graphic?.attributes?.KuduTractID;
            clickedTractId = typeof clickedKuduId === 'string' && clickedKuduId ? clickedKuduId : null;
            void refreshSelectedTractEffect();

            const gid =
                attrs?.GlobalID ??
                attrs?.globalid ??
                attrs?.GLOBALID ??
                attrs?.globalId ??
                attrs?.KuduTractID ??
                attrs?.KuduTractId ??
                attrs?.KUDUTractId ??
                attrs?.KUDUTRACTID ??
                attrs?.kudutractid;

            return { gid, allAttrs };
        };

        // click event handling
        let clickHandle: __esri.Handle | undefined;
        if (tractsGroupLayer) {
            clickHandle = view.on('click', async (evt: __esri.ViewClickEvent) => {
                const result = await handleTractHit(evt);
                if (!result) {
                    return;
                }

                const { gid, allAttrs } = result;

                if (gid && onSelectKuduTractID) {
                    onSelectKuduTractID(gid);
                }
                if (gid && onTractSelect && allAttrs) {
                    onTractSelect(gid, allAttrs);
                }
            });
        }

        // Zooms to one tract, with room around it, and returns its feature (undefined if it isn't
        // found). Any tract layer answers: they share features, and the project's config may have
        // turned Contact Category off.
        const zoomToTract = async (tractId: string): Promise<__esri.Graphic | undefined> => {
            const queryLayer = (tractsGroupLayer.layers.find((l) => l.visible) ?? tractsGroupLayer.layers.getItemAt(0)) as
                | __esri.GeoJSONLayer
                | undefined;
            if (!queryLayer) {
                return undefined;
            }
            await queryLayer.when();
            const query = queryLayer.createQuery();
            query.where = `KuduTractID = '${tractId.replace(/'/g, `''`)}'`;
            query.returnGeometry = true;
            const graphic = (await queryLayer.queryFeatures(query)).features?.[0];
            if (!graphic?.geometry?.extent) {
                return undefined;
            }
            await view.goTo(graphic.geometry.extent.expand(2));
            return graphic;
        };
        if (zoomToTractRef) {
            zoomToTractRef.current = (tractId: string) => {
                zoomToTract(tractId).catch((err) => console.log('Could not zoom to tract:', err));
            };
        }

        // Linger to peek: when the mouse rests on a tract for half a second, fade in the tract's ID and
        // what its color means on the visible layer (each piece of a combination with its swatch). It
        // describes the topmost tract, or a selected tract the mouse is inside (see peekTractId). Any
        // movement, press, scroll, drag or map movement fades it out, so it never trails the cursor;
        // resting again brings it back.
        let hoverTimer: number | undefined;
        let hoverFadeTimer: number | undefined;
        let hoverSeq = 0;
        // Tips shown so far, so a fade-out doesn't remove a tip that replaced the fading one.
        let hoverTipCount = 0;
        const hideHoverTip = () => {
            window.clearTimeout(hoverTimer);
            hoverSeq++;
            if (hoverTipShownRef.current) {
                hoverTipShownRef.current = false;
                const shown = hoverTipCount;
                setHoverTip((tip) => (tip ? { ...tip, leaving: true } : tip));
                window.clearTimeout(hoverFadeTimer);
                hoverFadeTimer = window.setTimeout(() => {
                    if (shown === hoverTipCount) {
                        setHoverTip(null);
                    }
                }, HOVER_FADE_MS);
            }
        };
        // The topmost tract under the mouse, unless the mouse is inside a selected tract drawn
        // underneath it (overlap cycling selects those): then that one, from the same layer.
        const peekGraphic = async (point: { x: number; y: number }, topmost: __esri.Graphic): Promise<__esri.Graphic> => {
            const topmostId = String(topmost.attributes?.KuduTractID ?? '');
            const selectedIds = selectedTractIdsRef.current;
            if (!topmost.layer || !peekNeedsSelectedLookup(topmostId, clickedTractId, selectedIds)) {
                return topmost;
            }
            const ids = [...(clickedTractId ? [clickedTractId] : []), ...selectedIds];
            const layerView = (await view.whenLayerView(topmost.layer)) as __esri.FeatureLayerView;
            const query = layerView.createQuery();
            query.geometry = view.toMap(point);
            query.spatialRelationship = 'intersects';
            query.where = `KuduTractID IN (${ids.map((kuduId) => `'${kuduId.replace(/'/g, `''`)}'`).join(',')})`;
            query.outFields = ['*'];
            query.returnGeometry = false;
            const under = (await layerView.queryFeatures(query)).features || [];
            const underIds = under.map((feature) => String(feature.attributes?.KuduTractID ?? ''));
            const pickedId = peekTractId(topmostId, clickedTractId, selectedIds, underIds);
            return pickedId === topmostId ? topmost : under[underIds.indexOf(pickedId)] ?? topmost;
        };
        // The tract the peek describes at a point, with the describer of the layer it's on.
        const peekAt = async (point: { x: number; y: number }): Promise<{ graphic: __esri.Graphic; describe: HoverDescriber } | undefined> => {
            const hit = await view.hitTest(point, { include: tractsGroupLayer.layers.toArray() });
            const topmost = (hit?.results?.[0] as __esri.HitTestResult['results'][number] | undefined)?.graphic;
            const describe = topmost?.layer ? hoverDescribersRef.current[topmost.layer.id] : undefined;
            return topmost && describe ? { graphic: await peekGraphic(point, topmost), describe } : undefined;
        };
        const peekId = (graphic: __esri.Graphic | undefined): string => String(graphic?.attributes?.KuduTractID ?? '');
        // Shows the peek once the mouse has rested at `point` for the delay; any move restarts it.
        const lingerAt = (point: { x: number; y: number }) => {
            window.clearTimeout(hoverTimer);
            const seq = ++hoverSeq;
            hoverTimer = window.setTimeout(async () => {
                try {
                    const peek = await peekAt(point);
                    const swatches = peek ? peek.describe(peek.graphic.attributes || {}) : [];
                    if (seq !== hoverSeq || swatches.length === 0) {
                        return;
                    }
                    hoverPointRef.current = point;
                    hoverTipShownRef.current = true;
                    hoverTipCount++;
                    // The tract's ID heads the tip, over what its color means.
                    setHoverTip({ tractId: peekId(peek.graphic) || undefined, swatches });
                } catch {
                    /* a hit-test racing a teardown or layer reload; nothing to show */
                }
            }, HOVER_PEEK_DELAY_MS);
        };
        const hoverHandles: __esri.Handle[] = [];
        if (tractsGroupLayer) {
            hoverHandles.push(
                view.on('pointer-move', (evt: __esri.ViewPointerMoveEvent) => {
                    hideHoverTip();
                    if (evt.pointerType === 'touch' || evt.buttons) {
                        return;
                    }
                    lingerAt({ x: evt.x, y: evt.y });
                }),
                view.on('pointer-leave', hideHoverTip),
                view.on('pointer-down', hideHoverTip),
                view.on('mouse-wheel', hideHoverTip),
                view.on('drag', hideHoverTip),
                // Zooms and pans from anywhere (buttons, Zoom to Tract, the keyboard) move the map under a still peek.
                view.watch('stationary', (stationary: boolean) => {
                    if (!stationary) {
                        hideHoverTip();
                    }
                })
            );
        }

        // Expose programmatic tract selection via ref
        if (selectTractRef && tractsGroupLayer) {
            selectTractRef.current = async (tractId: string) => {
                try {
                    const graphic = await zoomToTract(tractId);
                    if (!graphic) {
                        return;
                    }

                    // Wait for view to finish
                    await new Promise<void>((resolve) => {
                        const check = () => {
                            if (view.stationary && !view.updating) {
                                resolve();
                            } else {
                                requestAnimationFrame(check);
                            }
                        };
                        check();
                    });

                    // Small delay to ensure tiles are rendered
                    await new Promise((resolve) => setTimeout(resolve, 300));

                    // Hit test at tract center using the same shared function
                    const center = graphic.geometry.extent.center;
                    const screenPoint = view.toScreen(center);
                    await handleTractHit({ x: screenPoint.x, y: screenPoint.y });
                } catch (err) {
                    console.log('Could not select tract on map:', err);
                }
            };
        }

        // render logic
        // Zoom: tracts > AOI > skip. The view opens on the lower 48 (see Mapping) and stays there when
        // there's nothing to zoom to.
        const readyHandle = watchUtils.whenTrueOnce(view, 'ready', async () => {
            const layerPromises: Promise<unknown>[] = [aoiLayer?.when(), tractsGroupLayer?.when()].filter(Boolean) as Promise<unknown>[];

            await Promise.all(layerPromises);

            try {
                // Any tract layer has every tract; the project's config may have turned some off.
                const tractLayer = (tractsGroupLayer.layers.find((l) => l.id === 'tracts-contact-category') ??
                    tractsGroupLayer.layers.find((l) => l.id === 'tracts-title-status') ??
                    tractsGroupLayer.layers.find((l) => l.visible)) as __esri.GeoJSONLayer | undefined;
                let extent: __esri.Extent | undefined;
                if (tractLayer) {
                    const { extent: e } = await tractLayer.queryExtent();
                    extent = e;
                    // The legend drawer stays tucked in until the tracts load. It opens first, and the
                    // zoom waits for it: the drawer narrows the map, and the tracts should fit what's left.
                    if (extent && !legendToggledRef.current) {
                        setIsLegendOpen(true);
                        await new Promise((resolve) => window.setTimeout(resolve, LEGEND_DRAWER_MS));
                    }
                } else if (aoiLayer) {
                    const { extent: e } = await aoiLayer.queryExtent();
                    extent = e;
                }
                if (extent) {
                    // A jump, not a fly-in from the lower 48: an animated zoom fetches basemap tiles at
                    // every level on the way. About 5% of room around the tracts: the view snaps to
                    // the basemap's zoom levels, each 2x the last, which could leave up to twice the
                    // room, so snapping is off for this one jump. Wheel and button zooms still snap.
                    // (The page's view is always the MapView built above.)
                    const constraints = (view as __esri.MapView).constraints;
                    constraints.snapToZoom = false;
                    try {
                        await view.goTo(extent.expand(1.05), { animate: false });
                    } finally {
                        constraints.snapToZoom = true;
                    }
                }
            } catch {
                /* noop */
            }
            setHatchWidth(hatchWidthForZoom(view.zoom));
            hatchZoomTracking = true;
        });

        // handling unrendering
        teardownRef.current = () => {
            readyHandle?.remove?.();
            clickHandle?.remove?.();
            hoverHandles.forEach((handle) => handle.remove());
            window.clearTimeout(hoverTimer);
            window.clearTimeout(hoverFadeTimer);
            hatchZoomHandle.remove();
            hatchVisibilityHandles.forEach((handle) => handle.remove());
            if (activeHighlight) {
                activeHighlight.remove();
                activeHighlight = null;
            }
            layerListRef.current?.destroy();
            layerListRef.current = null;
            view.ui.remove(scalebar);
            if (selectionToolbarHostRef.current) {
                try {
                    view.ui.remove(selectionToolbarHostRef.current);
                } catch {
                    /* noop */
                }
            }
            selectionHighlightRef.current?.remove();
            selectionHighlightRef.current = null;
            sketchVMRef.current?.destroy();
            sketchVMRef.current = null;
            applyHighlightRef.current = null;
            if (zoomToTractRef) {
                zoomToTractRef.current = null;
            }
            try {
                map.remove(sketchLayer);
                map.remove(selectionOutlineLayer);
            } catch {
                /* noop */
            }
            try {
                if (layersToAdd.length) {
                    map.removeMany(layersToAdd);
                }
            } catch {
                /* noop */
            }
        };
    };

    // Re-apply the selection highlight whenever the selection changes. The function
    // is set inside handleLoad once the view is ready; before that it's a noop.
    React.useEffect(() => {
        applyHighlightRef.current?.();
    }, [selectedTractIds]);

    // Toggle the rectangle drawing tool when the user clicks the Select button.
    // `sketchReady` is set inside handleLoad once the SketchViewModel exists, so
    // pressing Select before the map is ready queues the intent and fires once
    // the view is up.
    React.useEffect(() => {
        const vm = sketchVMRef.current;
        if (!sketchReady || !vm) {
            return;
        }
        if (isSelectMode) {
            vm.create('rectangle');
        } else {
            try {
                vm.cancel();
            } catch {
                /* no active create */
            }
        }
    }, [isSelectMode, sketchReady]);

    // final jsx. The map container must stay mounted for every status -- ArcGIS is handed this
    // node directly, so it can't be swapped out for a loading/error element.
    return (
        <div style={{ display: 'flex', flex: 1, minWidth: 0 }}>
            <div className="map-area">
                <div ref={mapNodeRef} className="full-width" style={{ height: '100%', width: '100%' }} />
                {overlapBadge && (
                    // key restarts the fade animation on every click
                    <div key={overlapBadge.seq} className="map-overlap-tip" style={{ left: overlapBadge.x + 14, top: overlapBadge.y + 14 }}>
                        {overlapBadge.index} of {overlapBadge.count}
                    </div>
                )}
                {hoverTip && (
                    // Placed by placeHoverTip where the mouse rests.
                    <div
                        ref={hoverTipElRef}
                        className={`map-hover-tip${hoverTip.swatches.length > 1 ? ' is-combo' : ''}${hoverTip.tractId ? ' has-id' : ''}${
                            hoverTip.leaving ? ' is-leaving' : ''
                        }`}
                    >
                        {hoverTip.tractId && <div className="map-hover-tip-id">{hoverTip.tractId}</div>}
                        {hoverTip.swatches.map((swatch, i) => (
                            <div key={`${i}-${swatch.label}`} className="map-hover-tip-row">
                                <span className="map-hover-tip-swatch" style={{ background: swatchCss(swatch.rgba) }} />
                                <span className="map-hover-tip-label">{swatch.label}</span>
                            </div>
                        ))}
                    </div>
                )}
                {/* A page that handles load failures (onLoadFailed) shows them itself. */}
                {(mapStatus === 'loading' || (mapStatus === 'failed' && !onLoadFailed)) && (
                    <div className="map-status-overlay">
                        {mapStatus === 'loading' ? 'Loading map…' : 'The map could not be loaded. See the console for details.'}
                    </div>
                )}
                {selectionToolbarHostRef.current &&
                    createPortal(
                        <div className="map-selection-toolbar">
                            <button
                                type="button"
                                className={`map-selection-button ${isSelectMode ? 'is-active' : ''}`}
                                onClick={() => setIsSelectMode((m) => !m)}
                                disabled={!sketchReady}
                                aria-pressed={isSelectMode}
                                aria-label={isSelectMode ? 'Cancel rectangle selection' : 'Select tracts by rectangle'}
                                title={isSelectMode ? 'Click to cancel selection' : 'Draw a rectangle to select tracts'}
                            >
                                {/* Two overlapping squares — recognizable "select multiple" icon.
                                    Both transparent so strokes cross visibly in the overlap. */}
                                <svg width="18" height="18" viewBox="0 0 18 18" fill="none" aria-hidden="true">
                                    <rect x="1.5" y="1.5" width="10" height="10" stroke="currentColor" strokeWidth="1.5" />
                                    <rect x="6.5" y="6.5" width="10" height="10" stroke="currentColor" strokeWidth="1.5" />
                                </svg>
                            </button>
                            {selectedTractIds.length > 0 && (
                                <div className="map-selection-info">
                                    <span className="map-selection-count">{selectedTractIds.length} selected</span>
                                    <button
                                        type="button"
                                        className="map-selection-clear"
                                        onClick={() => setSelectedTractIds([])}
                                        title="Clear selection"
                                    >
                                        Clear
                                    </button>
                                </div>
                            )}
                        </div>,
                        selectionToolbarHostRef.current
                    )}
                <button
                    type="button"
                    className="map-legend-toggle"
                    onClick={() => {
                        legendToggledRef.current = true;
                        setIsLegendOpen((o) => !o);
                    }}
                    aria-label={isLegendOpen ? 'Hide legend' : 'Show legend'}
                    aria-expanded={isLegendOpen}
                >
                    {isLegendOpen ? '‹' : '›'}
                </button>
            </div>
            <aside className={`map-legend-drawer ${isLegendOpen ? 'is-open' : ''}`} aria-hidden={!isLegendOpen}>
                <div className="map-legend-header">Legend</div>
                <div ref={legendContainerRef} className="map-legend-content" />
            </aside>
        </div>
    );
};

export default MapLegacy;



// WEBPACK FOOTER //
// ./src/components/project/sections/GISComponents/MapLegacy.tsx